import fetcher from '@src/lib/fetcher';
import type { AxiosResponse } from 'axios';
import type { UsersIntergrationType } from './pages/connection/types';
import type { DynamicsBcUserSettings, RentmanUserSettings } from './pages/settings/types';

//User calls

//Get all users
export const getUsers = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/get-users`, 'GET', false);
};

//Delete user
export const deleteAccount = async (userId: number): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/delete-user`, 'DELETE', false, {
    userId,
  });
};

//Create user
export const createAccount = async (email: string, company: string, password: string, role: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/create-account`, 'POST', false, {
    email,
    company,
    password,
    role,
  });
};

//Update user
export const updateUser = async (userId: number, email: string, company: string, role: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/update-user`, 'PUT', false, {
    userId,
    email,
    company,
    role,
  });
};

//Login user
export const login = async (email: string, password: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/login`, 'POST', false, {
    email,
    password,
  });
};

//Login user as admin
export const loginAsUser = async (userId: number, token: string): Promise<AxiosResponse | null> => {
  return await fetcher(
    `${process.env.REACT_APP_API_URL}/users/login-as-user`,
    'POST',
    false,
    {
      userId,
    },
    { Authorization: `Bearer ${token}` },
  );
};

//Forgot password
export const forgotPass = async (email: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/forgot-password`, 'POST', false, { email });
};

//Update password
export const updatePassword = async (token: string, newPassword: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/reset-password`, 'POST', false, { token, newPassword });
};

//Verify password reset token
export const verifyResetToken = async (token: string): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/verify-reset-token`, 'POST', false, { token });
};

//Users integration
export const integrationByType = async (type: 'rentman' | 'dynamicsBc', selecting?: 'all' | 'api' | 'data'): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/integrations/filter/${type}${selecting ? `?selecting=${selecting}` : ''}`, 'GET', false);
};

export const allUsersIntegration = async (selecting?: 'all' | 'api' | 'data'): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/integrations/all${selecting ? `?selecting=${selecting}` : ''}`, 'GET', false);
};

export const setAllUsersIntegration = async (config: UsersIntergrationType, type: 'rentman' | 'dynamicsBc'): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/integrations/create/${type}`, 'POST', false, config);
};

export const updateUserIntegration = async (type: string, data: DynamicsBcUserSettings | RentmanUserSettings): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/users/integrations/update/default/data`, 'PUT', false, {
    type,
    data: { users_integrations_default_data: data },
  });
};

// Rentman
export const getUserIntegrationByUserIdAndType = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/rentman/test`, 'GET', false);
};

export const stockLocationsByUser = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/rentman/stock/locations/all`, 'GET', false);
};

export const ledgerCodesByUser = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/rentman/ledger/codes/all`, 'GET', false);
};

// Dynamics
export const allShipmentMethods = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/shipmentmethods/all`, 'POST', false);
};

export const allCompanies = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/companies/all`, 'GET', false);
};

export const allPaymentTerms = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/paymentterms/all`, 'GET', false);
};

export const allUnitsOfMeasure = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/unitsofmeasure/all`, 'POST', false);
};

export const allProductPostingGroups = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/productpostinggroups/all`, 'GET', false);
};

export const allDepartments = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/departments/all`, 'GET', false);
};

// Invoices
export const allInvoices = async (limit?: number, page?: number): Promise<AxiosResponse | null> => {
  const queryString = limit || page ? `/${limit || ''}/${page || ''}` : '';
  return await fetcher(`${process.env.REACT_APP_API_URL}/invoices/all${queryString}`, 'GET', false);
};

export const resyncInvoice = async (invoiceId: number): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/rentman/resync`, 'POST', false, { invoiceId });
};

export const resyncPayment = async (paymentId: number): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/payments/${paymentId}/resync`, 'POST', false);
};

//Payments
export const allPayments = async (limit?: number, page?: number): Promise<AxiosResponse | null> => {
  const queryString = limit || page ? `/${limit || ''}/${page || ''}` : '';
  return await fetcher(`${process.env.REACT_APP_API_URL}/payments/all${queryString}`, 'GET', false);
};

export const resyncCustomer = async (id: number): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/customers/${id}/resync`, 'POST', false);
};

export const allCustomers = async (limit = 25, page = 0): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/customers/all/${limit}/${page}`, 'GET', false);
};

// Logs
// Invoices
export const allLogs = async (limit?: number, page?: number): Promise<AxiosResponse | null> => {
  const queryString = limit || page ? `/${limit || ''}/${page || ''}` : '';
  return await fetcher(`${process.env.REACT_APP_API_URL}/logs/all${queryString}`, 'GET', false);
};

export const searchedLogs = async (limit?: number, page?: number, searchQuery?: string, selectedType?: string): Promise<AxiosResponse | null> => {
  const path = limit || page ? `/search/${limit || ''}/${page || ''}` : '/search';

  const queryParams: Record<string, string> = {};

  if (searchQuery) {
    queryParams.search = searchQuery;
  }
  if (selectedType) {
    queryParams.type = selectedType;
  }

  const baseUrl = `${process.env.REACT_APP_API_URL}/logs`;
  const urlObj = new URL(`${baseUrl}${path}`);

  Object.keys(queryParams).forEach((key) => {
    urlObj.searchParams.append(key, queryParams[key]);
  });

  return await fetcher(urlObj.toString(), 'GET', false);
};

// Test Connection
export const testConnection = async (type: 'rentman' | 'dynamicsBc'): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/${type}/connection/test`, 'GET', false);
};

export const getDynamicsWebhooks = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/webhooks`, 'GET', false);
};

export const setDynamicsWebhookEnabled = async (type: 'customers' | 'contacts' | 'generalLedgerEntries', enabled: boolean): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/webhooks/${type}`, 'PUT', false, { enabled });
};

export const activateDynamicsCustomerWebhook = async (force = false): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/webhooks/customers`, 'POST', false, force ? { force: true } : undefined);
};

export const activateDynamicsContactWebhook = async (force = false): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/webhooks/contacts`, 'POST', false, force ? { force: true } : undefined);
};

export const activateDynamicsLedgerWebhook = async (force = false): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/webhooks/generalLedgerEntries`, 'POST', false, force ? { force: true } : undefined);
};

export const customerFoldersByUser = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/rentman/customer-folders`, 'GET', false);
};

export const importCustomerLinksByUser = async (): Promise<AxiosResponse | null> => {
  return await fetcher(`${process.env.REACT_APP_API_URL}/dynamicsBc/customer-links`, 'POST', false);
};
